import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import router from '@/common/router';
import { useBaseMenuStore } from '@/common/stores/base-menu';
import type { MenuInfo } from '@/common/components/organisms/baseMenu/types';
import type { TabPanel } from '@/common/utils/types';

export const setup = () => {
  const { menuVisitRoute } = storeToRefs(useBaseMenuStore());

  // sub-header 노출은 그룹(menuVisitRoute[1])의 showSubHeader 플래그로 결정한다(현재 K8s 모니터링 그룹만 true).
  // 노출 그룹의 subMenuList를 평탄화하되 섹션(래퍼) 경계 정보를 보존한다.
  // 섹션 래퍼(Batch/OpenShift: path 없이 children 보유)는 children으로 펼치고, 그 첫 항목 앞에 구분선(|)을 표시한다.
  const sectionedLeaves = computed<{ leaf: MenuInfo; dividerBefore: boolean }[]>(() => {
    const group = menuVisitRoute.value[1];
    if (!group?.showSubHeader) {
      return [];
    }
    const result: { leaf: MenuInfo; dividerBefore: boolean }[] = [];
    (group?.subMenuList ?? []).forEach((entry) => {
      const isWrapper = !entry.path && !!entry.children?.length;
      const children = isWrapper ? (entry.children ?? []) : [entry];
      children
        .filter((item) => Boolean(item.path))
        .forEach((leaf, i) => {
          result.push({ leaf, dividerBefore: isWrapper && i === 0 && result.length > 0 });
        });
    });
    return result;
  });

  const leaves = computed<MenuInfo[]>(() => sectionedLeaves.value.map((section) => section.leaf));

  const tabs = computed<TabPanel<string>[]>(() =>
    sectionedLeaves.value.map((section) => ({
      text: section.leaf.text,
      value: section.leaf.value,
      dividerBefore: section.dividerBefore,
    })),
  );

  // 활성 탭 = 현재 경로의 leaf 메뉴(menuVisitRoute 마지막)
  const activeValue = computed<string>(() => menuVisitRoute.value.at(-1)?.value ?? '');

  // 탭이 1개 이상이면 노출 (단일 항목 그룹도 sub-header 바 유지 — 전 그룹 일관성)
  const isVisible = computed<boolean>(() => tabs.value.length > 0);

  const onSelectTab = (value: string) => {
    const leaf = leaves.value.find((item) => item.value === value);
    if (leaf?.path && leaf.path !== router.currentRoute.value.fullPath) {
      router.push(leaf.path);
    }
  };

  return { tabs, activeValue, isVisible, onSelectTab };
};
